import { LitElement, html, css } from 'lit'
import { property, customElement } from 'lit/decorators.js'
import type { PopupButtonAttributes } from 'zaptic-popup-button'
import classNames from 'classnames'
import { booleanAttributeSchema, oneOfStringsAttributeSchema } from './helpers/yup'
import type { Schema } from 'yup'

type PopupButtonColor = PopupButtonAttributes['color']

export const popupButtonColors = ['purple', 'red', 'green', 'white']
const colorAttributeSchema = oneOfStringsAttributeSchema(...popupButtonColors) as Schema<PopupButtonColor>

const convertColorAttributeToProperty = (value: unknown): PopupButtonColor | null => {
    try {
        return colorAttributeSchema.validateSync(value)
    } catch (ignore) {
        return null
    }
}

const convertBooleanAttributeToProperty = (value: unknown): boolean => {
    try {
        return booleanAttributeSchema.validateSync(value)
    } catch (ignore) {
        return false
    }
}

@customElement('zaptic-popup-button')
export class PopupButton extends LitElement implements PopupButtonAttributes {
    static styles = css`
        :host {
            display: block;
        }

        button {
            color: white;
            border-radius: 100px;
            margin: 5px 0;
            border: 1px solid transparent;
            padding: var(--PopupButton-padding, 15px);
            width: 100%;
            font-size: 16px;
            font-weight: var(--font-weight-medium);
            cursor: pointer;
            outline: none;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        button.purple {
            background-color: var(--purple);
        }

        button.red {
            background-color: var(--red);
        }

        button.green {
            background-color: var(--green);
        }

        button.white {
            background-color: white;
            color: var(--darkGrey);
            border-color: var(--darkGrey);
        }

        button.disabled {
            background-color: var(--grey8);
            color: var(--grey5);
            border-color: var(--grey5);
            cursor: auto;
        }
    `

    @property({
        type: String,
        reflect: true,
        converter: convertColorAttributeToProperty,
    })
    color: PopupButtonColor = 'purple'

    @property({
        type: Boolean,
        reflect: true,
        converter: convertBooleanAttributeToProperty,
    })
    disabled = false

    render() {
        return html`<button class="${classNames(this.color, { disabled: this.disabled })}"><slot></slot></button>`
    }
}
